import { Injectable } from '@angular/core';
import { ModalService, ModalType } from '@ipreo/ngx-sprinkles';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { BidResultsModalComponent } from '../components/bid-results/bid-results-modal.component';
import {
  BidResultsState,
  BidResultsWithType
} from '../state/bid-results/state';
import { selectBidResults, SharedState } from '../state/reducer';
import { ManagerGridViewModel } from '../types/deal.viewmodel.types';
import { Globals } from './globals';
import { createModal } from '../utils/modal';

@Injectable()
export class BidResultsService {
  private bidResultsState$: Observable<BidResultsState> = this.store.pipe(
    select(selectBidResults)
  );

  private bidResults$: Observable<BidResultsWithType> =
    this.bidResultsState$.pipe(
      map(({ bidResults }) => {
        if (bidResults) {
          this.bidResults = bidResults;
        }
        return bidResults;
      })
    );

  public winningBidResultOrionId$: Observable<number> = this.bidResults$.pipe(
    map((bidResults) => {
      const winningBidResult =
        bidResults && bidResults.bids.find((bid) => bid.winningBid);
      return winningBidResult && winningBidResult.orionId;
    })
  );

  private bidResultsError$: Observable<string> = this.bidResultsState$.pipe(
    map(({ error }) => error)
  );

  public isBidResultsUnavailable$: Observable<boolean> = combineLatest([
    this.bidResults$,
    this.bidResultsError$
  ]).pipe(
    map(
      ([bidResults, error]: [BidResultsWithType, string]) =>
        !bidResults || !!error || (!!bidResults && !bidResults.bids.length)
    ),
    distinctUntilChanged()
  );

  private bidResults = {} as BidResultsWithType;

  constructor(
    private store: Store<SharedState>,
    protected modalService: ModalService,
    private globals: Globals
  ) { }

  public handleModal(bidResults?, deal?, fromBidCompare?): void {
    if (fromBidCompare) {
      this.globals.nativeWindow.document.body.classList.add(
        'bid-compare-overlay'
      );
    } else {
      this.globals.nativeWindow.document.body.classList.remove(
        'bid-compare-overlay'
      );
    }

    const _bidResults = bidResults || this.bidResults;

    this.openBidResultsModal(_bidResults, deal, fromBidCompare);
  }

  private openBidResultsModal(
    bidResults: BidResultsWithType,
    deal: ManagerGridViewModel | undefined,
    fromBidCompare: boolean
  ) {
    const regularRows =
      bidResults && bidResults.bids ? bidResults.bids.length - 1 : 0;
    //if deal header is included - add its height to the modal height
    const dealHeaderHeight = deal ? 42 : 0;

    const seriesSelectorHeight =
      bidResults?.bids?.some((b) => (b.series?.length ?? 0) > 1) ? 35 : 0;

    const modalHeight = regularRows * 31 + 166 + dealHeaderHeight + seriesSelectorHeight;
    const windowHeight = this.getWindowHeight(fromBidCompare);

    // Extract extended fields available at runtime when deal is BidcompIssueGridViewModel
    const dealAny = deal as any;
    const bondNoteType: string | undefined = dealAny?.bondNoteType;
    const dealSeries: Array<{ seriesId: number; name?: string | null }> | undefined =
      dealAny?.series;

    return createModal(this.modalService, {
      type: ModalType.View,
      view: BidResultsModalComponent,
      showOverlay: true,
      closeOnOverlayClick: true,
      context: { bidResults, deal, fromBidCompare, bondNoteType, dealSeries },
      modalClass: 'bid-results-modal',
      modalContentClass: fromBidCompare
        ? 'compare-bid-results-modal'
        : 'bid-results-modal-drag',
      inputs: {
        width: 650,
        height: modalHeight < windowHeight ? modalHeight : windowHeight - 160
      }
    }).instance.response;
  }

  private getWindowHeight(fromBidCompare: boolean) {
    const container = this.globals.nativeWindow.document.querySelector(
      'muni-bidcomp-shared-compare'
    ) as HTMLElement;

    if (fromBidCompare) {
      return container.offsetHeight;
    }

    const windowHeight =
      window.innerHeight ||
      window.document.documentElement.clientHeight ||
      window.document.body.clientHeight;
    return windowHeight;
  }
}
